import { Box, Typography, useTheme } from "@mui/material";
import { Dayjs } from "dayjs";
import SkeletonSlot from "./SkeletonSlot";
import AvailabilityItem from "./AvailabilityItem";
import { CalendarAvailability } from "../utils/calendarUtils";
import { useAvailabilities } from "../AvailabilitiesProvider";

interface DayCellProps {
  day: Dayjs;
  isToday: boolean;
  availabilities: CalendarAvailability[];
}

export default function DayCell({
  day,
  isToday,
  availabilities,
}: DayCellProps) {
  const theme = useTheme();
  const { isLoading, setSelectedDate } = useAvailabilities();
  const sortedAvailabilities = availabilities
    ?.filter(
      (slot) => !slot.isFareharbor || !(slot.availability as any).private,
    )
    .sort((a, b) => {
      const aTime = a.isFareharbor
        ? (a.availability as any).start_at
        : a.availability.start_time;
      const bTime = b.isFareharbor
        ? (b.availability as any).start_at
        : b.availability.start_time;
      return new Date(aTime).getTime() - new Date(bTime).getTime();
    });
  return (
    <Box
      onClick={() => setSelectedDate(day.format("YYYY-MM-DD"))}
      sx={{
        border: "1px solid #e0e0e0",
        p: 0.5,
        backgroundColor: "#fff",
        minHeight: "120px",
        position: "relative",
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "#fafafa",
        },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: isToday ? 700 : 500,
          color: isToday ? theme.palette.primary.contrastText : "#333",
          bgcolor: isToday ? theme.palette.primary.main : "inherit",
          mb: 0.5,
          ml: "auto",
          width: "24px",
          height: "24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 2,
        }}
      >
        {day.date()}
      </Typography>

      {isLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <SkeletonSlot />
          <SkeletonSlot />
          <SkeletonSlot />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            maxWidth: "200px",
          }}
        >
          {sortedAvailabilities?.slice(0, 4).map((availability) => (
            <AvailabilityItem
              key={`${availability.item.id}-${availability.availabilityId}`}
              availability={availability}
            />
          ))}

          {sortedAvailabilities && sortedAvailabilities.length > 4 && (
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: theme.palette.primary.main,
                fontWeight: 600,
                textAlign: "center",
                mt: 0.5,
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              +{sortedAvailabilities.length - 4} more
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
